import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

interface Notification {
  id: string;
  type: 'level_up' | 'achievement_done' | 'reward_claimed' | 'info';
  title: string;
  body: string;
  icon: string;
  readAt: string | null;
  createdAt: string;
}

export function NotificationBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const list = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<Notification[]>('/api/notifications'),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const items = list.data ?? [];
  const unread = items.filter((n) => !n.readAt).length;

  const readAll = useMutation({
    mutationFn: () => api('/api/notifications/read-all', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  function handleOpen() {
    setOpen((v) => {
      const next = !v;
      if (next && unread > 0) readAll.mutate();
      return next;
    });
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        className="relative rounded-full border border-border-soft bg-bg-elevated/50 p-2 text-neutral-300 transition hover:text-neutral-100"
        title="Bildirimler"
      >
        <span>🔔</span>
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-ach-500 px-1 text-[9px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 overflow-hidden rounded-lg border border-border-soft bg-bg-raised shadow-xl">
          <div className="border-b border-border-soft px-4 py-2 text-xs font-semibold text-neutral-300">
            Bildirimler
          </div>
          <ul className="max-h-80 overflow-y-auto">
            {items.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-neutral-500">Henüz yok</li>
            )}
            {items.map((n) => (
              <li key={n.id}
                  className={`flex items-start gap-3 border-b border-border-soft/40 px-4 py-3 last:border-b-0 ${
                    !n.readAt ? 'bg-brand-500/5' : ''
                  }`}>
                <span className="text-xl">{n.icon}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-neutral-100">{n.title}</p>
                  {n.body && <p className="line-clamp-2 text-xs text-neutral-400">{n.body}</p>}
                  <p className="mt-1 text-[10px] text-neutral-500">
                    {new Date(n.createdAt).toLocaleString('tr-TR')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
